import { createSelector } from "reselect"
import { get, find, findKey, chain, omit, map } from "lodash"
import { getFactors, getSelectedMetric } from "./metrics"
import { getModelCategories } from "selectors/modelMetadata"
import { getFavoriteFactors } from "modules/portfolio-root/selectors/portfolios"
import { percentageFormatter } from "helpers/formatters/numbers"
import { tableDownloadDataReducer } from "helpers/comparison"
import { getData0, getData1, getData2, getData3, getData4 } from "./data"
import { getItems } from "./items"

const EMPTY_ARRAY = []

const getSelectedDate = (_, props) => props.selectedDate
const getIsSingleDate = (_, props) => props.hideGraph

function getDailyData(selectedDate, data) {
  const date = find(data, d => d.date === selectedDate)
  if (!date) {
    return null
  }

  return {
    factors: chain(date)
      .get("factors")
      .keyBy("id")
      .mapValues("value")
      .value(),
  }
}

const getDailyData0 = createSelector([getSelectedDate, getData0], getDailyData)
const getDailyData1 = createSelector([getSelectedDate, getData1], getDailyData)
const getDailyData2 = createSelector([getSelectedDate, getData2], getDailyData)
const getDailyData3 = createSelector([getSelectedDate, getData3], getDailyData)
const getDailyData4 = createSelector([getSelectedDate, getData4], getDailyData)

const getFactorsData = createSelector(
  [getSelectedDate, getModelCategories, getFavoriteFactors, getData0, getData1, getData2, getData3, getData4],
  (selectedDate, categories, favoriteFactors, data, data1, data2, data3, data4) => {
    const date = find(data, d => d.date === selectedDate)
    if (!date) {
      return null
    }

    // Get all the other factors for the other items at the selected date, flattened. We'll use that to fill
    // the top X factors per categories if we won't have at least X factors returned on the main item
    const allOtherDatesFactors = chain([data1, data2, data3, data4])
      .map(dataDates => chain(dataDates)
        .find(d => d.date === selectedDate)
        .get("factors")
        .value())
      .compact()
      .flatten()
      .value()

    const allFactors = chain(date)
      .get("factors")
      .map(f => ({ ...f, priorityOrder: 1 }))
      .concat(allOtherDatesFactors)
      .uniqBy(item => `${item.id}${item.category}`)
      .value()

    return chain(categories)
      .filter(category => !category.singleFactor)
      .map(category => ({
        id: category.id,
        factors: chain(allFactors)
          .filter(f => f.category === category.id)
          .map(factor => ({
            ...omit(factor, "name"),
            label: factor.name,
            fn: item => percentageFormatter(get(item, ["factors", factor.id], null)),
            favorite: !!favoriteFactors[factor.id],
          }))
          .value(),
      }))
      .keyBy("id")
      .mapValues("factors")
      .value()
  },
)

export const getDetailsItems = createSelector(
  [getFactors, getSelectedMetric, getIsSingleDate],
  (factors, selectedMetric, isSingleDate) => {
    if (!factors[selectedMetric.id]) {
      return EMPTY_ARRAY
    }
    return [{
      items: [{
        label: selectedMetric.label,
        hide: isSingleDate,
        fn: item => percentageFormatter(get(item, ["factors", selectedMetric.id], null)),
      }],
    }]
  },
)

export const getDetailsData = createSelector(
  [getDailyData0, getDailyData1, getDailyData2, getDailyData3, getDailyData4],
  (data0, data1, data2, data3, data4) => ([data0, data1, data2, data3, data4]),
)

export const getFactorsCategories = createSelector(
  [getModelCategories, getFactorsData, getFactors, getFavoriteFactors],
  (categories, factors, factorsMeta, favoriteFactors) => chain(categories)
    .map((category) => ({
      ...category,
      ...(category.singleFactor && {
        getter: item => percentageFormatter(get(item, ["factors", findKey(factorsMeta, f => f.category === category.id)])),
      }),
      factors: chain(factors)
        .get(category.id)
        .map(factor => ({
          ...factor,
          getter: factor.fn,
          favorite: get(favoriteFactors, factor.id, false),
        }))
        .orderBy(["favorite", f => f.priorityOrder || 0, f => Math.abs(f.value)], ["desc", "desc", "desc"])
        .value(),
    }))
    .value(),
)

export const getTableDetailsData = createSelector(
  [getItems, getDetailsData, getDetailsItems],
  (items, data, detailsItems) => tableDownloadDataReducer(items, data, detailsItems, "items"),
)

const getTableFactorsData = createSelector(
  [getItems, getDetailsData, getFactorsCategories],
  (items, data, factorsCategories) => tableDownloadDataReducer(items, data, factorsCategories, "factors"),
)

export const getTableDownloadData = createSelector(
  [getItems, getTableDetailsData, getTableFactorsData],
  (items, tableDetailsData, tableFactorsData) => {
    const headers = ["Metric Group", "Metric", ...map(items, "label")]
    return {
      sheets: [{
        name: "Factor Sensitivity",
        rows: [
          headers,
          ...tableDetailsData,
          ...tableFactorsData,
        ],
      }],
    }
  },
)
